import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@shared/api/client';

/**
 * Persisted user-level settings: UI theme and preferred language.
 *
 * They sit side by side in the user menu but are different kinds of
 * data and live in two different places on the backend, so they go
 * through two different endpoints and are never combined:
 *
 *   theme     -> PATCH /user/preferences/appearance   (preferences dictionary,
 *                the same store as the table column settings)
 *   language  -> PATCH /user                          (field on the user itself)
 *
 * Both are JSON Patch with exactly ONE replace op — only the field
 * being changed travels, nothing else on the resource is touched.
 *
 * The HTTP client resolves 4xx/5xx as `{ ok: false }` rather than
 * throwing, so each thunk converts that into a rejection: callers
 * (and the slices' optimistic revert) key off `result.error`.
 */

export const THEME_PREFERENCES = {
  light: 'light',
  dark: 'dark',
  auto: 'auto',
};

export const isThemePreference = value =>
  Object.prototype.hasOwnProperty.call(THEME_PREFERENCES, value);

// `previous` is carried in the argument so the slice can roll back on
// rejection without the component keeping its own copy of the state.
export const saveAppearance = createAsyncThunk(
  'userSettings/saveAppearance',
  async ({ theme }, { rejectWithValue }) => {
    const response = await client.patch('/user/preferences/appearance', [
      { op: 'replace', path: '/theme', value: theme },
    ]);
    if (!response.ok) {
      return rejectWithValue({ status: response.status, data: response.data });
    }
    return response;
  }
);

export const saveUserLanguage = createAsyncThunk(
  'userSettings/saveUserLanguage',
  async ({ preferredLanguage }, { rejectWithValue }) => {
    const response = await client.patch('/user', [
      { op: 'replace', path: '/preferredLanguage', value: preferredLanguage },
    ]);
    if (!response.ok) {
      return rejectWithValue({ status: response.status, data: response.data });
    }
    return response;
  }
);
